import {useTimeoutFn} from 'react-use'

import ChevronRightSvg from '@/assets/icons/chevron-right.svg?react'
import ProductCard from '@/components/ProductCard/ProductCard'
import Slider from '@/components/Slider/Slider'
import {useLocale} from '@/lib/locale/locale'
import {getProductQueryKey} from '@/lib/product/hooks/useProduct'
import {FlashDealsRoute} from '@/routeRegistry'
import {getFlashDeals} from '@/services/homeService'
import VisuallyHidden from '@/utils/a11y/VisuallyHidden'
import {NO_REFETCH_OPTIONS} from '@/utils/query/constants'
import {FLASH_DEALS_TARGET_DATE} from '@/views/FlashDeals/FlashDeals'

import Timer from './components/Timer'
import style from './HomePageFlashDeals.module.scss'

const DEFAULT_FLASH_DEALS_TITLE = 'Christmas Sales'

export function useFlashDeals() {
  const [locale] = useLocale()
  const queryClient = useQueryClient()

  const query = useQuery({
    queryKey: ['flashDeals', locale],
    queryFn: async ({signal}) => getFlashDeals({locale, signal}),
    ...NO_REFETCH_OPTIONS,
  })

  ;(function syncCache() {
    if (!query.data) return
    query.data.products.forEach(product => {
      queryClient.setQueryData(
        getProductQueryKey({retailer: product.retailer, id: product.id, locale}),
        product,
      )
    })
  })()

  return query
}

export default memo(function HomePageFlashDeals() {
  const {data: flashDeals, refetch} = useFlashDeals()

  const remainingSeconds = flashDeals ? flashDeals.remainingSeconds : 0

  useTimeoutFn(
    () => {
      void refetch()
    },
    remainingSeconds * 1000 - 500,
  )

  const isFlashDeals = new Date() >= FLASH_DEALS_TARGET_DATE

  const shouldShow = isFlashDeals && !!flashDeals

  return (
    <div className={clsx(style.FlashDealsWrapper, !shouldShow && style.HighMargin)}>
      <div className={clsx(style.FlashDealsTitle, !shouldShow && style.LowMargin)}>
        <h2>
          <span aria-hidden>🔥&nbsp;</span>
          {shouldShow ? flashDeals.title : DEFAULT_FLASH_DEALS_TITLE}
        </h2>
        {!shouldShow && (
          <div className={style.FlashDealsTimerLabel} aria-hidden>
            Starts in
          </div>
        )}
        <VisuallyHidden>{!shouldShow ? 'Starts in' : 'Ends in'}</VisuallyHidden>
        <Timer
          key={remainingSeconds}
          remainingSeconds={
            shouldShow
              ? remainingSeconds
              : Math.floor((FLASH_DEALS_TARGET_DATE.getTime() - Date.now()) / 1000)
          }
        />
        {shouldShow && (
          <Link to={FlashDealsRoute.fullPath} aria-label={`View more ${flashDeals.title}`}>
            <div className={style.FlashDealsViewMore}>View more</div>
            <div className={style.FlashDealsViewMoreMobile}>
              <ChevronRightSvg />
            </div>
          </Link>
        )}
      </div>
      {shouldShow && flashDeals.products.length > 0 && (
        <Slider
          wrapperProps={{
            'className': style.FlashDeals,
            'role': 'list',
            'aria-label': flashDeals.title,
          }}
        >
          {flashDeals.products.map((product, i) => {
            return (
              <div role='listitem' key={i}>
                <ProductCard product={product} headingLevel={3} />
              </div>
            )
          })}
        </Slider>
      )}
      {shouldShow && flashDeals.products.length === 0 && (
        <div className={style.FlashDealsEmpty}>
          Our deals are currently unavailable. Please come back later!
        </div>
      )}
    </div>
  )
})
